import { liteClient as algoliasearch } from 'algoliasearch/lite'
import {
  addDoc,
  collection,
  deleteField,
  doc,
  documentId,
  getDoc,
  getDocs,
  getDocsFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type Query,
  type DocumentData,
  type Unsubscribe,
  deleteDoc,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import {
  fromFirestoreActivity,
  toFirestoreActivityPatch,
} from '@core/services/news/converters/activity-explore-converter'
import { NewsErrorConverter } from '@core/services/news/converters/news-error-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  ActivityExplore,
  CreateActivityExploreInput,
  UpdateActivityExplorePatch,
} from '@domain/models/activity-explore'
import type { NewsCategory } from '@domain/models/news-category'
import type { UserProfile } from '@domain/models/user'

const COLLECTION = 'activities_explore'
const NEWS_CATEGORIES_COLLECTION = 'news_categories'
const INDEX_NAME = 'pd_news'
const APP_ID = import.meta.env.VITE_ALGOLIA_APPLICATION_ID as string
const API_KEY = import.meta.env.VITE_ALGOLIA_API_KEY as string

export interface PaginatedResult {
  activities: ActivityExplore[]
  lastDoc: QueryDocumentSnapshot | null
  hasMore: boolean
}

export type NewsFetchPolicy = 'default' | 'server'

function toOptionalTrimmedString(value: string | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

async function getQuerySnapshot(
  queryRef: Query<DocumentData>,
  fetchPolicy: NewsFetchPolicy = 'default',
) {
  if (fetchPolicy === 'server') {
    return getDocsFromServer(queryRef)
  }
  return getDocs(queryRef)
}

export class NewsService {
  private static instance: NewsService

  private constructor() {}

  public static getInstance(): NewsService {
    if (!NewsService.instance) {
      NewsService.instance = new NewsService()
    }
    return NewsService.instance
  }

  private getCollection() {
    if (!firestore) {
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Firestore is not initialized.',
      })
    }
    return collection(firestore, COLLECTION)
  }

  public async createActivityExplore(
    input: CreateActivityExploreInput,
    currentUser: UserProfile,
  ): Promise<string> {
    if (!currentUser.uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for creating activity.',
      })
    }

    try {
      const payload = toFirestoreActivityPatch({
        title: input.title,
        newsCategoryId: input.newsCategoryId,
        newsCategoryString: input.newsCategoryString,
        mainCategoryId: input.mainCategoryId,
        mainCategoryString: input.mainCategoryString,
        image: input.image,
        publishTime: new Date(),
        article: input.article,
        articleMarkdown: input.articleMarkdown,
        sourceUrl: input.sourceUrl,
        containsYoutubeVideo: input.containsYoutubeVideo,
        youtubeLink: input.youtubeLink,
        likeCount: 0,
        commentsCount: 0,
        importanceScore: input.importanceScore,
        videoUrl: input.videoUrl,
        filters: input.filters,
        belongsToUser: {
          name: currentUser.profile_slug,
          profilePhoto: currentUser.photo_url,
          userRefId: currentUser.uid,
          location: currentUser.map_location ?? null,
        },
        translations: input.translations,
        titleTranslations: input.titleTranslations,
        isTranslated: input.isTranslated,
        orgRefId: input.orgRefId,
        promoted: input.promoted,
        recommended: input.recommended,
      })

      const createdDoc = await addDoc(this.getCollection(), payload)
      return createdDoc.id
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async updateActivityExplore(
    activityId: string,
    patch: UpdateActivityExplorePatch,
    currentUser: UserProfile,
    existingActivity?: ActivityExplore,
  ): Promise<void> {
    if (!currentUser.uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for updating activity.',
      })
    }

    try {
      const targetActivity = existingActivity ?? await this.fetchActivity(activityId)
      const ownerId = targetActivity.belongsToUser?.userRefId
      const isOwner = Boolean(ownerId && ownerId === currentUser.uid)
      const isUserAdmin = currentUser.admin === true || currentUser.isAdmin === true
      if (!isUserAdmin && !isOwner) {
        throw new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: 'Current user is not allowed to manage this activity.',
        })
      }

      const payload = toFirestoreActivityPatch({
        title: patch.title,
        newsCategoryId: patch.newsCategoryId,
        newsCategoryString: patch.newsCategoryString,
        mainCategoryId: patch.mainCategoryId,
        mainCategoryString: patch.mainCategoryString,
        image: patch.image,
        article: patch.article,
        articleMarkdown: patch.articleMarkdown,
        sourceUrl: patch.sourceUrl,
        containsYoutubeVideo: patch.containsYoutubeVideo,
        youtubeLink: patch.youtubeLink,
        videoUrl: patch.videoUrl,
        importanceScore: patch.importanceScore,
        filters: patch.filters,
        translations: patch.translations,
        titleTranslations: patch.titleTranslations,
        isTranslated: patch.isTranslated,
        promoted: patch.promoted,
        recommended: patch.recommended,
        orgRefId: patch.orgRefId,
      })
      const normalizedArticle = toOptionalTrimmedString(patch.article)
      if (patch.article !== undefined && normalizedArticle === undefined) {
        payload['article'] = deleteField()
      }
      const normalizedArticleMarkdown = toOptionalTrimmedString(
        patch.articleMarkdown,
      )
      if (
        patch.articleMarkdown !== undefined &&
        normalizedArticleMarkdown === undefined
      ) {
        payload['article_markdown'] = deleteField()
      }

      if (Object.keys(payload).length === 0) {
        return
      }

      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }
      await updateDoc(doc(firestore, COLLECTION, activityId), payload)
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async deleteActivityExplore(
    activityId: string,
    currentUser: UserProfile,
    existingActivity?: ActivityExplore,
  ): Promise<void> {
    if (!currentUser.uid) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for deleting activity.',
      })
    }

    try {
      const targetActivity = existingActivity ?? await this.fetchActivity(activityId)
      const ownerId = targetActivity.belongsToUser?.userRefId
      const isOwner = Boolean(ownerId && ownerId === currentUser.uid)
      const isUserAdmin = currentUser.admin === true || currentUser.isAdmin === true
      if (!isUserAdmin && !isOwner) {
        throw new CoreError({
          cause: CoreErrorCause.PermissionDenied,
          context: 'Current user is not allowed to manage this activity.',
        })
      }

      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }
      await deleteDoc(doc(firestore, COLLECTION, activityId))
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async fetchLatestActivities(
    count = 4,
    fetchPolicy: NewsFetchPolicy = 'default',
  ): Promise<ActivityExplore[]> {
    try {
      const q = query(
        this.getCollection(),
        orderBy('publish_time', 'desc'),
        limit(count),
      )
      const snapshot = await getQuerySnapshot(q, fetchPolicy)
      return snapshot.docs.map((item) => fromFirestoreActivity(item))
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivity(activityId: string): Promise<ActivityExplore> {
    try {
      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }
      const docRef = doc(firestore, COLLECTION, activityId)
      const snapshot = await getDoc(docRef)
      if (!snapshot.exists()) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: `Activity "${activityId}" was not found.`,
        })
      }
      return fromFirestoreActivity(snapshot)
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivitiesByIds(ids: string[]): Promise<ActivityExplore[]> {
    if (ids.length === 0) {
      return []
    }

    try {
      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }

      // Firestore 'in' queries support max 30 items per batch.
      const batchSize = 30
      const results: ActivityExplore[] = []

      for (let index = 0; index < ids.length; index += batchSize) {
        const batch = ids.slice(index, index + batchSize)
        const q = query(this.getCollection(), where(documentId(), 'in', batch))
        const snapshot = await getDocs(q)
        snapshot.docs.forEach((item) => {
          results.push(fromFirestoreActivity(item))
        })
      }

      // Preserve the order from Algolia (relevance-ranked).
      const orderMap = new Map(ids.map((id, idx) => [id, idx]))
      results.sort(
        (a, b) => (orderMap.get(a.uid) ?? 0) - (orderMap.get(b.uid) ?? 0),
      )

      return results
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async fetchActivitiesPaginated(
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
    newsCategoryId?: string | null,
    fetchPolicy: NewsFetchPolicy = 'default',
  ): Promise<PaginatedResult> {
    try {
      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }

      const constraints: QueryConstraint[] = []

      if (newsCategoryId) {
        constraints.push(where('news_category_id', '==', newsCategoryId))
      }

      constraints.push(orderBy('publish_time', 'desc'))

      if (lastDocument) {
        constraints.push(startAfter(lastDocument))
      }

      constraints.push(limit(pageSize))

      const q = query(this.getCollection(), ...constraints)
      const snapshot = await getQuerySnapshot(q, fetchPolicy)

      const activities = snapshot.docs.map((item) => fromFirestoreActivity(item))

      const lastDoc =
        snapshot.docs.length > 0
          ? snapshot.docs[snapshot.docs.length - 1]
          : null

      return {
        activities,
        lastDoc,
        hasMore: snapshot.docs.length >= pageSize,
      }
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async fetchRecommendedByNewsCategory(
    newsCategoryId: string,
    count = 25,
  ): Promise<ActivityExplore[]> {
    try {
      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }

      const recommendedQuery = query(
        this.getCollection(),
        where('recommended', '==', true),
        where('news_category_id', '==', newsCategoryId),
        orderBy('like_count', 'desc'),
        limit(count),
      )
      const snapshot = await getDocs(recommendedQuery)
      return snapshot.docs.map((item) => fromFirestoreActivity(item))
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public observeActivityExplore(
    activityId: string,
    callback: (activity: ActivityExplore | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    if (!firestore) {
      onError?.(
        new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        }),
      )
      return () => {}
    }

    return onSnapshot(
      doc(firestore, COLLECTION, activityId),
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null)
          return
        }
        callback(fromFirestoreActivity(snapshot))
      },
      (error) => {
        onError?.(NewsErrorConverter.domainModel(error))
      },
    )
  }

  public async fetchNewsCategories(): Promise<NewsCategory[]> {
    try {
      if (!firestore) {
        throw new CoreError({
          cause: CoreErrorCause.Unknown,
          context: 'Firestore is not initialized.',
        })
      }

      const categoriesQuery = query(
        collection(firestore, NEWS_CATEGORIES_COLLECTION),
        orderBy('order'),
      )
      const snapshot = await getDocsFromServer(categoriesQuery)
      return snapshot.docs
        .map((item) => {
          const data = item.data() as Record<string, unknown>
          const isActive = typeof data['isActive'] === 'boolean'
            ? data['isActive']
            : typeof data['is_active'] === 'boolean'
              ? data['is_active']
              : true

          const order = typeof data['order'] === 'number'
            ? data['order']
            : undefined

          const mainCategoryId = typeof data['main_category_id'] === 'number'
            ? data['main_category_id']
            : undefined

          return {
            uid: item.id,
            ...data,
            name: typeof data['name'] === 'string' ? data['name'] : '',
            order,
            isActive,
            mainCategoryDocId:
              typeof data['main_category_doc_id'] === 'string'
                ? data['main_category_doc_id']
                : undefined,
            mainCategoryId,
          } satisfies NewsCategory
        })
        .filter((item) => item.isActive !== false)
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }

  public async searchPdNews(searchQuery: string): Promise<string[]> {
    if (!searchQuery.trim()) {
      return []
    }

    if (!APP_ID || !API_KEY) {
      console.warn('Algolia credentials not configured')
      return []
    }

    try {
      const client = algoliasearch(APP_ID, API_KEY)
      const { results } = await client.search({
        requests: [{ indexName: INDEX_NAME, query: searchQuery, hitsPerPage: 50 }],
      })

      const firstResult = results[0]
      if (!firstResult || !('hits' in firstResult)) {
        return []
      }

      return firstResult.hits.map((hit: { objectID: string }) => hit.objectID)
    } catch (error) {
      throw NewsErrorConverter.domainModel(error)
    }
  }
}

const newsService = NewsService.getInstance()

export async function fetchLatestActivities(
  count = 4,
  fetchPolicy: NewsFetchPolicy = 'default',
): Promise<ActivityExplore[]> {
  return newsService.fetchLatestActivities(count, fetchPolicy)
}

export async function fetchActivity(
  activityId: string,
): Promise<ActivityExplore> {
  return newsService.fetchActivity(activityId)
}

export async function fetchActivitiesByIds(
  ids: string[],
): Promise<ActivityExplore[]> {
  return newsService.fetchActivitiesByIds(ids)
}

export async function fetchActivitiesPaginated(
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot | null,
  newsCategoryId?: string | null,
  fetchPolicy: NewsFetchPolicy = 'default',
): Promise<PaginatedResult> {
  return newsService.fetchActivitiesPaginated(
    pageSize,
    lastDocument,
    newsCategoryId,
    fetchPolicy,
  )
}

export async function createActivityExplore(
  input: CreateActivityExploreInput,
  currentUser: UserProfile,
): Promise<string> {
  return newsService.createActivityExplore(input, currentUser)
}

export async function updateActivityExplore(
  activityId: string,
  patch: UpdateActivityExplorePatch,
  currentUser: UserProfile,
  existingActivity?: ActivityExplore,
): Promise<void> {
  return newsService.updateActivityExplore(
    activityId,
    patch,
    currentUser,
    existingActivity,
  )
}

export async function deleteActivityExplore(
  activityId: string,
  currentUser: UserProfile,
  existingActivity?: ActivityExplore,
): Promise<void> {
  return newsService.deleteActivityExplore(activityId, currentUser, existingActivity)
}

export function observeActivityExplore(
  activityId: string,
  callback: (activity: ActivityExplore | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return newsService.observeActivityExplore(activityId, callback, onError)
}

export async function fetchRecommendedByNewsCategory(
  newsCategoryId: string,
  count = 25,
): Promise<ActivityExplore[]> {
  return newsService.fetchRecommendedByNewsCategory(newsCategoryId, count)
}

export async function fetchNewsCategories(): Promise<NewsCategory[]> {
  return newsService.fetchNewsCategories()
}

export async function searchPdNews(query: string): Promise<string[]> {
  return newsService.searchPdNews(query)
}
